/* eslint-disable array-bracket-newline */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IDexTrade, enums } from '@kattana-io/common';
import { IStore } from '../../../reducers';
import { switchCurrentTicker } from '../../../Authlogic';
import { Loader } from '../../DexTrades';
import useCollectTickers from '../../DexTrades/useCollectTickers';
import useWalletInfo from './useWalletInfo';
import StatsTable from './StatsTable';
import TradesTable from './TradesTable';
import TradesTableMobile from './mobile/TradesTable';
import ErrorPlaceholder from '../ErrorPlaceholder';
import NoDataPlaceholder from '../NoDataPlaceholder';
import { CustomSelect } from '../../../molecules/Dropdown';
import useDevice from '../../../pages/MainPage/useMobileDetect';
import { ETableTimeframe, parseTableTimeframe } from '../../DexTrades/helpers';
import { onCopyAddress, onWalletScan } from '../../../helpers/addressUtils';
import { formatAddress } from '../../../helpers/formatters';

import './styles.scss';

const DISABLED_TIMEFRAMES = [
  ETableTimeframe.WEEK.toLowerCase(),
  ETableTimeframe.MONTH.toLowerCase(),
];

const TIMEFRAMES_WITH_DATE = [
  ETableTimeframe.LATEST,
  ETableTimeframe.DAY,
  ETableTimeframe.WEEK,
  ETableTimeframe.MONTH,
];

const TIMEFRAMES_WITHOUT_DATE = [
  ETableTimeframe.DAY,
  ETableTimeframe.WEEK,
  ETableTimeframe.MONTH,
];

interface IWalletModalProps {
  actionCloseModal: () => void;
  windowProps?: any;
}

export const getChainsArrayFromTradesArray = (trades: IDexTrade[]) => {
  const protocolsSet = new Set([
    ...trades.map((item: IDexTrade) => item.chain),
  ]);

  const protocolsArr = Array.from(protocolsSet);
  return protocolsArr;
};

const WalletModal = ({ actionCloseModal, windowProps }: IWalletModalProps) => {
  const modalProps = useSelector((state: IStore) => state.modal.props);
  const plan = useSelector((state: IStore) => state.features.currentPlan);

  const props = windowProps || modalProps;
  const { wallet, address, timeframe, history, chain, date, timezone } = props;

  const [modalTimeframe, setModalTimeframe] = useState(timeframe);
  const { isMobile } = useDevice();
  const params = useParams();

  const collection = useMemo(() => {
    return [{ chain, pair: address }];
  }, [chain, address]);

  const { tickers, loadingTickers } = useCollectTickers(collection);

  const { isErrorNoTrades, isServerError, trades, stats, loading } =
    useWalletInfo({ address, chain, wallet, timeframe: modalTimeframe, date });

  const ticker = tickers[`${chain}-${address}`];

  const [base, quote] = ticker?.name.split('/') || ['', ''];

  const calcVolume = (trades: IDexTrade[]) =>
    trades.reduce((cons, trade) => cons + +trade.value_usd, 0);

  const calcProfit = (trades: IDexTrade[]) =>
    trades.reduce((prev: number, trade: IDexTrade) => {
      if (trade.buy) {
        return prev - Number(trade.value_usd);
      } else {
        return prev + Number(trade.value_usd);
      }
    }, 0);

  // const onWalletClick = () => {
  //   actionShowModal(ModalTypes.walletPairs, {
  //     wallet,
  //     timeframe,
  //     history: history ? [props, ...history] : [props],
  //   });
  // };

  // const onBackClick = () => {
  //   const goBack = props.history[0];
  //   actionShowModal(ModalTypes.walletPairs, goBack);
  // };

  const onPairNameClick = () =>
    switchCurrentTicker({
      address,
      chain,
    });

  const isValid = stats && Object.keys(stats).length > 0 && trades.length > 0;
  const pairName = ticker?.flipped ? `${quote}/${base}` : `${base}/${quote}`;
  const timeframes = date ? TIMEFRAMES_WITH_DATE : TIMEFRAMES_WITHOUT_DATE;

  return (
    <div className='wallet_modal no_scroll'>
      <div className='wallet_modal__header'>
        {/* {props.history && (
          <div className='wallet_modal__back' onClick={onBackClick} />
        )} */}
        <span>
          <div className='wallet_modal__wallet__title'>
            {isMobile ? formatAddress(wallet) : wallet}
          </div>
          &nbsp;on&nbsp;
          <span
            onClick={onPairNameClick}
            className='wallet_modal__wallet__pair_name'
          >
            {pairName}
          </span>
        </span>
        <div className='wallet_modal__header__actions'>
          <div
            className='wallet_modal__pill wallet_modal__secondary icon-copy'
            title='Copy address'
            onClick={() => onCopyAddress(wallet)}
          />
          <div
            className='wallet_modal__pill wallet_modal__secondary icon-share'
            title='Share link'
            onClick={() =>
              onCopyAddress(
                `${window.location.origin}/top-traders/${chain}/${address}/${wallet}/${timeframe}`
              )
            }
          />
          <div
            className='wallet_modal__pill wallet_modal__secondary icon-top-right-square'
            title='Open scan'
            onClick={() => onWalletScan(wallet, chain)}
          />
          <Link
            to={`/en/wallet-overview/${params.chain || 'ETH'}/${wallet}`}
            title='Open in Magic wallet analyzer'
            className='wallet_modal__pill wallet_modal__primary icon-wallet'
            onClick={actionCloseModal}
          />
          <div className='wallet_modal__timeframe_select'>
            <CustomSelect
              onSelect={setModalTimeframe}
              disabledOptions={
                plan !== enums.PricingPlan.premium ? DISABLED_TIMEFRAMES : []
              }
              disabledReason='Available only for premium subscription'
              options={timeframes.map((item) => ({
                value: parseTableTimeframe(item),
                label: item,
              }))}
              initial={{
                value: modalTimeframe,
                label:
                  modalTimeframe[0].toUpperCase() + modalTimeframe.slice(1),
              }}
              width={80}
              height={28}
            />
          </div>
        </div>
      </div>
      <div className='wallet_modal__close' onClick={actionCloseModal} />
      <div className='wallet_modal__container'>
        {loading || loadingTickers ? (
          <Loader />
        ) : (
          <>
            {isServerError && <ErrorPlaceholder />}
            {(isErrorNoTrades || !isValid || !stats) && <NoDataPlaceholder />}
            {isValid && !isServerError && !isErrorNoTrades && stats && (
              <StatsTable
                stats={stats}
                base={base}
                quote={quote}
                ticker={ticker}
                wallet={wallet}
                timezone={timezone}
                volume={calcVolume(trades)}
                profit={calcProfit(trades)}
                flipped={ticker?.flipped}
              />
            )}
          </>
        )}

        {!loading && !loadingTickers && isValid && (
          <>
            {isMobile ? (
              <TradesTableMobile
                flipped={ticker?.flipped}
                timezone={timezone}
                base={base}
                quote={quote}
                trades={trades}
                base_decimals={ticker.base_decimals}
                quote_decimals={ticker.quote_decimals}
                chain={ticker.chain}
              />
            ) : (
              <div className='trades_table table_desktop__container'>
                <TradesTable
                  flipped={ticker.flipped}
                  timezone={timezone}
                  base={base}
                  quote={quote}
                  trades={trades}
                  base_decimals={ticker.base_decimals}
                  quote_decimals={ticker.quote_decimals}
                  chain={ticker.chain}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WalletModal;
